<h2 mat-dialog-title class="cut-long-text">
    Testaufgabe <span class="name">{{ this.data.name }} </span>bearbeiten<span class="taskname">{{ taskName() | transloco }}</span>
</h2>
<br />
<mat-divider></mat-divider>
<mat-dialog-content class="content-container">
    @if (!playerService.iFrameReady) {
        <div class="spinner-overlay">
            <div class="spinner-overlay-content">
                <pixl-spinner></pixl-spinner>
            </div>
        </div>
    }

    <as-split direction="horizontal" class="split-container">
        <as-split-area #iframePane class="iframe-pane" [minSize]="getLeftAreaMinSize()" [size]="getLeftAreaSize()">
            <iframe #playerPreviewIframe [src]="iframePreviewUrl!" (load)="onIFrameLoaded()" tabindex="0" title="Player 2"></iframe>
        </as-split-area>
        @if (editorPaneVisible()) {
            <as-split-area class="editor-pane" [minSize]="25" [size]="50">
                <!--
                    Dynamic editor area: each exercise type can register its own component here.
                    The component is loaded dynamically via NgComponentOutlet –
                    set `activeEditorComponent` and `activeEditorInputs` in handlePlayerEvents().
                    New exercise types: create a dedicated component under edit-exercise/<type>/ and
                    register it in the EnumerationPart-/TextPart-branch of handlePlayerEvents().
                    If no type-specific editor is set (activeEditorComponent === null),
                    the generic pixl-editor is used as a fallback.
                -->
                <ng-template #editorContent>
                    @if (activeEditorComponent()) {
                        <ng-container
                            [ngComponentOutlet]="activeEditorComponent()!"
                            [ngComponentOutletInputs]="activeEditorInputs()"
                        ></ng-container>
                    } @else {
                        <pixl-editor
                            [htmlString]="explanationContentHtml"
                            [editorHeight]="'98%'"
                            [bearerToken]="authToken!"
                            [assetUrl]="assetsUrl"
                            [mediaUrl]="mediaUrl"
                            [(editedHtmlString)]="explanationContentHtml"
                            (callbackFct)="callbackFct($event)"
                            [fixedMenu]="true"
                            [showAccounting]="accounting()"
                            [primaryColor]="playerContentMessage.config.playerSettings?.theme?.primaryColor ?? null"
                            [secondaryColor]="playerContentMessage.config.playerSettings?.theme?.secondaryColor ?? null"
                            [tertiaryColor]="playerContentMessage.config.playerSettings?.theme?.tertiaryColor ?? null"
                            [placeholderColor]="playerContentMessage.config.playerSettings?.theme?.placeholderColor ?? null"
                            [hoverColor]="playerContentMessage.config.playerSettings?.theme?.hoverColor ?? null"
                            [drawColor]="playerContentMessage.config.playerSettings?.theme?.drawColor ?? null"
                            [drawHighlightColor]="playerContentMessage.config.playerSettings?.theme?.drawHighlightColor ?? null"
                            [compareSolutionsBgColor]="playerContentMessage.config.playerSettings?.theme?.compareSolutionsBgColor ?? null"
                        >
                        </pixl-editor>
                    }
                </ng-template>
                @if (showAnyAdditionalTab()) {
                    <mat-tab-group>
                        <mat-tab label="{{ 'edit' | transloco }}">
                            <ng-container [ngTemplateOutlet]="editorContent"></ng-container>
                        </mat-tab>
                        @if (showQuantityErrorsTab()) {
                            <mat-tab label="{{ 'quantity_errors_tab' | transloco }}">
                                <div class="quantity-errors-layout">
                                    <div class="quantity-errors-ranges">
                                        @for (range of quantityErrorRanges(); track range.from; let i = $index) {
                                            <div
                                                class="quantity-error-range"
                                                [class.active]="activeRangeIndex() === i"
                                                tabindex="0"
                                                (click)="onRangeActive(range, i)"
                                                (keyup.enter)="onRangeActive(range, i)"
                                            >
                                                {{ range.from }} – {{ range.to }} %
                                            </div>
                                        }
                                    </div>
                                    <div class="quantity-errors-form">
                                        <mat-form-field>
                                            <mat-label>{{ 'quantity_errors_message' | transloco }}</mat-label>
                                            <textarea
                                                matInput
                                                [value]="messageText()"
                                                (input)="messageText.set($any($event.target).value)"
                                                rows="4"
                                            ></textarea>
                                        </mat-form-field>
                                        <button
                                            mat-raised-button
                                            [disabled]="isSaveMessageDisabled()"
                                            (click)="saveMessage()"
                                        >{{ 'quantity_errors_ok' | transloco }}</button>
                                    </div>
                                </div>
                            </mat-tab>
                        }
                    </mat-tab-group>
                } @else {
                    <ng-container [ngTemplateOutlet]="editorContent"></ng-container>
                }
            </as-split-area>
        }
    </as-split>
</mat-dialog-content>

@if (dialogData.type === 'explanation') {
    <div class="explanation-toolbar">
        <div class="explanation-pagination">
            <button mat-icon-button
                    [disabled]="currentPageIndex() === 0"
                    (click)="goToPage(currentPageIndex() - 1)">
                <mat-icon>chevron_left</mat-icon>
            </button>
            <span>{{ currentPageIndex() + 1 }} / {{ playerContentMessage.learnItem.pages.length }}</span>
            <button mat-icon-button
                    [disabled]="currentPageIndex() === playerContentMessage.learnItem.pages.length - 1"
                    (click)="goToPage(currentPageIndex() + 1)">
                <mat-icon>chevron_right</mat-icon>
            </button>
        </div>
        <div class="explanation-buttons">
            <button mat-raised-button color="warn"
                    [disabled]="playerContentMessage.learnItem.pages.length <= 1"
                    (click)="deletePage()">
                <mat-icon>delete</mat-icon>
                {{ 'explanation_delete_page' | transloco: {nr: currentPageIndex() + 1} }}
            </button>
            <button mat-raised-button color="primary"
                    [disabled]="playerContentMessage.learnItem.pages.length <= 1"
                    (click)="openPagesDialog()">
                <mat-icon>menu</mat-icon>
                {{ 'explanation_manage_pages' | transloco }}
            </button>
            <button mat-raised-button color="primary" (click)="addPage()">
                <mat-icon>add</mat-icon>
                {{ 'explanation_page_add' | transloco }}
            </button>
        </div>
        <div class="explanation-options">
            <mat-form-field appearance="outline" class="layout-select">
                <mat-label>{{ 'explanation_layout' | transloco }}</mat-label>
                <mat-select
                        [value]="playerContentMessage.learnItem.pages[currentPageIndex()]?.layout ?? '1col'"
                        (selectionChange)="changeLayout($event.value)">
                    <mat-option value="1col">{{ 'explanation_layout_1col' | transloco }}</mat-option>
                    <mat-option value="1col_mCol">{{ 'explanation_layout_1col_mcol' | transloco }}</mat-option>
                    <mat-option value="2col">{{ 'explanation_layout_2col' | transloco }}</mat-option>
                </mat-select>
            </mat-form-field>
            <mat-checkbox
                    [checked]="!!playerContentMessage.learnItem.pages[currentPageIndex()]?.showMarginalColumnOnMobile"
                    (change)="toggleMarginalColumn()">
                {{ 'explanation_show_marginal_mobile' | transloco }}
            </mat-checkbox>
        </div>
    </div>
}
<mat-divider></mat-divider>
<mat-dialog-actions>
    <button mat-raised-button (click)="onCancel()">{{ "cancel" | transloco }}</button>
    <button mat-raised-button (click)="onPreview()"><mat-icon matTooltip="visibility">visibility</mat-icon>{{ "player_preview" | transloco }}</button>
    <button mat-raised-button color="primary" (click)="onSave()">{{ "save" | transloco }}</button>
</mat-dialog-actions>
